import { useStore } from '@doltech/core/lib/entity/store';
import { isNotEmpty } from '@doltech/utils/lib/text';
import { s3ToCDN } from '@doltech/utils/lib/url';
import { sum } from 'lodash';
import { Instance, types as t } from 'mobx-state-tree';
import * as React from 'react';

import { AudioViewModeEnum } from '../../media-constant';
import { getFileType } from '@doltech/utils/lib/file-upload';

export const FileModel = t.model('FileModel', {
  id: t.maybeNull<any>(t.string),
  name: t.maybeNull<any>(t.string),
  width: t.maybeNull<any>(t.number),
  height: t.maybeNull<any>(t.number),
  size: t.maybeNull(t.number),
  uploadedDate: t.maybeNull<any>(t.string),
  path: t.maybeNull<any>(t.string),
  url: t.maybeNull<any>(t.string),
  videoUrl: t.maybeNull<any>(t.string),
  frameUrl: t.maybeNull<any>(t.string),
  alt: t.maybeNull<any>(t.string),
  caption: t.maybeNull<any>(t.string),
  title: t.maybeNull<any>(t.string),
  downloadTxt: t.maybeNull<any>(t.string),
  viewMode: t.maybeNull<any>(t.string),
  uploadProcess: t.optional(t.number, 0),
  type: t.maybeNull<any>(t.string),
});
export const FileUpload = t
  .model('FileUpload', {
    files: t.optional(t.array(FileModel), []),
    initialed: t.optional(t.boolean, false),
  })
  .actions((self) => ({
    updateProcess(process: number, id) {
      const found = self.files.find((file) => file.id === id);
      if (!found) return;
      found.uploadProcess = process;
    },
    initMedia(medias) {
      self.initialed = true;
      self.files.replace(medias);
    },
    upsertMedias(medias) {
      medias.forEach((media) => {
        const isExist = self.files.find((item) => item.id === media.id);
        if (!isExist) {
          self.files.push(media);
        }
      });
    },
    upsertFile(fileInfo) {
      let found = self.files.find((file) => file.id === fileInfo.id);
      if (found) {
        found = fileInfo;
      } else {
        self.files.push(fileInfo);
      }
    },
    isFileIdExist(id) {
      return Boolean(self.files.find((file) => file.id === id));
    },
    updateUrl(id, url) {
      const found = self.files.find((file) => file.id === id);
      if (found) {
        found.videoUrl = url;
      } else {
        self.files.push({ id, url });
      }
    },
    createUniqueFile(id) {
      if (self.files.find((file) => file.id === id)) return;
      self.files.push({ id });
    },
    createFile(id) {
      self.files.push({ id });
    },
    createDocFile(id, videoUrl) {
      self.files.push({ id, videoUrl });
    },
    createAudioFile(id) {
      self.files.push({ id, viewMode: AudioViewModeEnum.SHORT });
    },
    updateAlt(id, alt) {
      const found = self.files.find((file) => file.id === id);
      if (found) {
        found.alt = alt;
      }
    },
    updateDownloadBtn(id, downloadTxt) {
      const found = self.files.find((file) => file.id === id);
      if (found) {
        if (isNotEmpty(downloadTxt)) {
          found.downloadTxt = downloadTxt;
        } else {
          found.downloadTxt = 'Download';
        }
      }
    },
    updateCaption(id, caption) {
      const found = self.files.find((file) => file.id === id);
      if (found) {
        found.caption = caption;
      }
    },
    updateTitle(id, title) {
      const found = self.files.find((file) => file.id === id);
      if (found) {
        found.title = title;
      }
    },
    updateViewMode(mode, id) {
      const found = self.files.find((file) => file.id === id);
      found.viewMode = mode;
    },
    updateFile(item: any) {
      const found = self.files.find((file) => file.id === item.id);
      const {
        tokenResponse: { path },
        file: { name, size, uploadedDate },
        id,
      } = item;
      if (!found) {
        return {
          url: s3ToCDN(path),
          name,
          size,
          uploadedDate,
          type: getFileType(name),
          path,
        };
      }
      found.name = name;
      found.size = size;
      found.uploadedDate = uploadedDate;
      found.path = path;
      found.url = s3ToCDN(path);
      found.id = id;
      const type = getFileType(name);
      found.type = type;

      return found;
    },
    cancelFileUpload(id) {
      const found = self.files.find((file) => file.id === id);
      found.name = undefined;
      found.size = undefined;
      found.url = undefined;
      found.uploadedDate = undefined;
      found.path = undefined;
      found.uploadProcess = 0;
    },
    clearUrl(id) {
      const found = self.files.find((file) => file.id === id);
      if (found) {
        found.url = undefined;
      }
    },
    removeFileUpload(id) {
      const found = self.files.find((file) => file.id === id);
      self.files.remove(found);
    },
  }))
  .views((self) => ({
    emptyUploadFile(id) {
      const found = self.files.find((file) => file.id === id);
      if (!found) {
        return true;
      }
      return !found.name && found.uploadProcess === 0 && !found.uploadedDate && !found.url;
    },
    hasFileUploaded(id) {
      const found = self.files.find((file) => file.id === id);
      if (!found) {
        return false;
      }
      return found.name && found.uploadedDate && found.url;
    },
    getFileById(id) {
      return self.files.find((file) => file.id === id);
    },
    getFileByIds(ids) {
      // why not use .filter -> because we want to keep the order of ids
      if (ids) {
        return ids
          .map((id) => {
            return self.files.find((f) => f.id === id);
          })
          .filter(Boolean);
      }
    },
    getPublicFileByIds(ids) {
      return ids
        .map((id) => {
          const file = { ...self.files.find((f) => f.id === id) };
          file.url = s3ToCDN(file.path);
          return file;
        })
        .filter(Boolean);
    },
  }))
  .views((self) => ({
    getAvgUploadProgress(ids) {
      const progressInNumber =
        sum(self.getFileByIds(ids).map((file) => file?.uploadProcess || 0)) /
        Math.max(ids.length, 1);
      return {
        number: progressInNumber,
        text: progressInNumber.toFixed(2),
      };
    },
  }));

export interface FileUploadInstance extends Instance<typeof FileUpload> {}

export const FileUploadStoreContext = React.createContext(null);
// Export hook.tsx to use
export const useFileUploadStore = <S extends FileUploadInstance>() => {
  const store = useStore<{ fileUploadStore: S }>(FileUploadStoreContext);
  if (store != null) {
    const { fileUploadStore } = store;
    return fileUploadStore;
  }
  return undefined;
};

export const withInitMediaInfos = (Component: any) => (props: any) => {
  const { mediaInfos, detailData } = props;
  const fileUploadStore = useFileUploadStore();
  const myMediaInfos = mediaInfos || detailData?.mediaInfos;

  if (
    fileUploadStore?.files?.length === 0 &&
    myMediaInfos?.length > fileUploadStore?.files?.length
  ) {
    fileUploadStore.initMedia(mediaInfos || detailData?.mediaInfos);
  }
  return <Component {...props} />;
};
